import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Plus, Minus, Calendar, X, Save, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Skeleton } from "@/components/ui/skeleton";

const daysOfWeek = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const dayKeyMap = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
};

const defaultOperatingHours = {
  monday: {
    is_available: false,
    start_time: "09:00",
    end_time: "17:00",
    break_times: [],
  },
  tuesday: {
    is_available: false,
    start_time: "09:00",
    end_time: "17:00",
    break_times: [],
  },
  wednesday: {
    is_available: false,
    start_time: "09:00",
    end_time: "17:00",
    break_times: [],
  },
  thursday: {
    is_available: false,
    start_time: "09:00",
    end_time: "17:00",
    break_times: [],
  },
  friday: {
    is_available: false,
    start_time: "09:00",
    end_time: "17:00",
    break_times: [],
  },
  saturday: {
    is_available: false,
    start_time: "09:00",
    end_time: "13:00",
    break_times: [],
  },
  sunday: {
    is_available: false,
    start_time: "09:00",
    end_time: "13:00",
    break_times: [],
  },
};

export default function ProviderAvailabilityForm({
  provider,
  onSave,
  onClose,
  isLoading = false,
}) {
  const [formData, setFormData] = useState({
    provider_name: provider?.provider_name || "",
    operating_hours: defaultOperatingHours,
    status: "active",
  });

  // Fetch current availability data
  const { data: availabilityData, isLoading: isLoadingAvailability } = useQuery({
    queryKey: ["doctorAvailability", provider?.id],
    queryFn: () => apiClient.get(`/api/users/doctors/${provider.id}/availability`),
    enabled: !!provider?.id,
  });

  // Update form data when availability data is loaded
  useEffect(() => {
    if (availabilityData?.availability) {
      const newOperatingHours = { ...defaultOperatingHours };

      // Convert the availability array into the operating_hours format
      availabilityData.availability.forEach((slot) => {
        const dayKey = dayKeyMap[slot.dayOfWeek];
        if (dayKey) {
          // Transform break times from camelCase to snake_case
          const transformedBreakTimes = (slot.breakTimes || []).map(breakTime => ({
            start_time: breakTime.startTime,
            end_time: breakTime.endTime,
          }));
          
          newOperatingHours[dayKey] = {
            is_available: true,
            start_time: slot.startTime,
            end_time: slot.endTime,
            break_times: transformedBreakTimes,
          };
        }
      });

      setFormData({
        provider_name: provider?.provider_name || "",
        operating_hours: newOperatingHours,
        status: "active",
      });
    }
  }, [availabilityData, provider?.provider_name]);

  const handleOperatingHoursChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleBreakTimeChange = (day, breakIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          break_times: prev.operating_hours[day].break_times.map((breakTime, index) =>
            index === breakIndex ? { ...breakTime, [field]: value } : breakTime
          ),
        },
      },
    }));
  };

  const addBreakTime = (day) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          break_times: [...prev.operating_hours[day].break_times, { start_time: "12:00", end_time: "13:00" }],
        },
      },
    }));
  };

  const removeBreakTime = (day, breakIndex) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          break_times: prev.operating_hours[day].break_times.filter((_, index) => index !== breakIndex),
        },
      },
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-5xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Provider Availability Settings
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          {isLoadingAvailability ? (
            <div className="space-y-6">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Provider Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Provider Information</h3>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{formData.provider_name}</p>
                <p className="text-sm text-slate-600">{provider?.email}</p>
              </div>
            </div>

            <Separator />

            {/* Operating Hours */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Operating Hours
              </h3>
              <div className="grid gap-4">
                {daysOfWeek.map((day) => (
                  <div
                    key={day.key}
                    className="p-4 bg-slate-50 rounded-lg space-y-3"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-24 font-medium">{day.label}</div>
                      <Switch
                        checked={formData.operating_hours[day.key].is_available}
                        onCheckedChange={(checked) =>
                          handleOperatingHoursChange(
                            day.key,
                            "is_available",
                            checked
                          )
                        }
                        data-testid={`provider-avail-${day.key}-switch`}
                      />
                    </div>
                    {formData.operating_hours[day.key].is_available && (
                      <div className="ml-28 space-y-3">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Label>From:</Label>
                            <Input
                              type="time"
                              value={formData.operating_hours[day.key].start_time}
                              onChange={(e) =>
                                handleOperatingHoursChange(
                                  day.key,
                                  "start_time",
                                  e.target.value
                                )
                              }
                              className="w-32"
                              data-testid={`provider-avail-${day.key}-start`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label>To:</Label>
                            <Input
                              type="time"
                              value={formData.operating_hours[day.key].end_time}
                              onChange={(e) =>
                                handleOperatingHoursChange(
                                  day.key,
                                  "end_time",
                                  e.target.value
                                )
                              }
                              className="w-32"
                              data-testid={`provider-avail-${day.key}-end`}
                            />
                          </div>
                        </div>
                        
                        {/* Break Times */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Break Times:</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addBreakTime(day.key)}
                              className="text-xs h-7"
                              data-testid={`provider-avail-${day.key}-add-break`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Break
                            </Button>
                          </div>
                          {formData.operating_hours[day.key].break_times.map((breakTime, breakIndex) => (
                            <div key={breakIndex} className="flex items-center gap-2">
                              <Input
                                type="time"
                                value={breakTime.start_time}
                                onChange={(e) =>
                                  handleBreakTimeChange(
                                    day.key,
                                    breakIndex,
                                    "start_time",
                                    e.target.value
                                  )
                                }
                                className="w-28"
                                data-testid={`provider-avail-${day.key}-break-${breakIndex}-start`}
                              />
                              <span className="text-slate-500 text-sm">to</span>
                              <Input
                                type="time"
                                value={breakTime.end_time}
                                onChange={(e) =>
                                  handleBreakTimeChange(
                                    day.key,
                                    breakIndex,
                                    "end_time",
                                    e.target.value
                                  )
                                }
                                className="w-28"
                                data-testid={`provider-avail-${day.key}-break-${breakIndex}-end`}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeBreakTime(day.key, breakIndex)}
                                className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                                data-testid={`provider-avail-${day.key}-break-${breakIndex}-remove`}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6">
              <Button type="button" variant="outline" onClick={onClose} data-testid="provider-avail-cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                data-testid="provider-avail-save-btn"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Provider Settings
                  </>
                )}
              </Button>
            </div>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
